import { Store } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAreasQuery } from '@/hooks/business/useAreasQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessUserQuery } from '@/hooks/business/useBusinessUserQuery'

interface AreaSelectorProps {
  selectedPointOfSale: string
  setSelectedPointOfSale: (value: string) => void
  // Extended props for flexibility
  label?: string
  placeholder?: string
  className?: string
  triggerClassName?: string
  excludeAreaId?: number
  autoSelectFirst?: boolean
  showIcon?: boolean
  disabled?: boolean
  id?: string
}

export const AreaSelector = ({
  selectedPointOfSale,
  setSelectedPointOfSale,
  // Default values for backward compatibility
  label = 'Punto de Venta',
  placeholder = 'Seleccionar punto de venta',
  className = 'flex flex-col gap-2',
  triggerClassName = 'w-full md:w-[250px]',
  excludeAreaId,
  autoSelectFirst = true,
  showIcon = true,
  disabled = false,
  id = 'point-of-sale',
}: AreaSelectorProps) => {
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})

  const {
    areas: areasRawData,
    isLoadingAreas,
    isErrorAreas,
  } = useAreasQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  const { user, allowRoles } = useBusinessUserQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  const areas = useMemo(() => {
    let filteredAreas = areasRawData

    // Filter by user permissions
    if (!allowRoles(['OWNER'], true)) {
      filteredAreas = areasRawData?.filter(
        (area) =>
          area.isActive &&
          user?.allowedSalesAreas?.some((item) => item.id === area.id)
      )
    } else {
      filteredAreas = areasRawData?.filter((area) => area.isActive)
    }

    // Exclude specific area if provided
    if (excludeAreaId) {
      filteredAreas = filteredAreas?.filter((area) => area.id !== excludeAreaId)
    }

    return filteredAreas
  }, [areasRawData, user, allowRoles, excludeAreaId])

  useEffect(() => {
    if (autoSelectFirst && areas && areas.length > 0 && !selectedPointOfSale) {
      setSelectedPointOfSale(areas[0].id.toString())
    }
  }, [areas, selectedPointOfSale, setSelectedPointOfSale, autoSelectFirst])

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} className="text-lg font-medium">
          {label}
        </Label>
      )}
      <Select
        value={selectedPointOfSale}
        onValueChange={setSelectedPointOfSale}
        disabled={
          disabled ||
          !areas ||
          areas.length === 0 ||
          isLoadingAreas ||
          isLoadingBusinessData ||
          isErrorAreas ||
          isErrorBusinessData
        }
      >
        <SelectTrigger id={id} className={triggerClassName}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {areas &&
            areas.length > 0 &&
            areas?.map((pos) => (
              <SelectItem
                key={pos.id}
                value={pos.id.toString()}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  {showIcon && (
                    <Store className="h-4 w-4 text-muted-foreground" />
                  )}
                  {pos.name}
                </div>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  )
}
